import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink, Lock } from 'lucide-react';
import { useInViewport } from '@lib/hooks/useInViewport';
import {
  getMetodosPago,
  iniciarPago,
  marcarInteresContactoMs2,
} from '@lib/services/portal.service';
import { PlanMockupCard } from './PlanMockupCard';

/**
 * Paso 5 del wizard — Pago. Solo se renderiza si el titular eligió MS2 en
 * el paso anterior. Replica el estilo full-bleed del paso 4 (gradient
 * celeste + hero con precio enorme + card mockup verde) para mantener
 * coherencia visual entre los dos pasos finales del wizard.
 *
 * Flow:
 *  1) Mostramos el plan elegido + monto + dos opciones:
 *     a) "Pagar" → POST /portal/legajo/:token/pago/iniciar → abre checkout
 *        en pestaña nueva y avanza al paso final.
 *     b) "Quiero que me contacten" → POST .../plan-superador/interes-contacto
 *        → marca interesContactoMs2 + revierte a MS1 (mismo endpoint que
 *        usa el botón equivalente del paso 4), y avanza al paso final.
 *  2) El cobro real se valida fuera del flow del wizard.
 */

interface Props {
  token: string;
  onBack: () => void;
  onContinue: () => void;
}

function fmtNumeroAR(n: number): string {
  return new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 }).format(n);
}

const COLOR_PRIMARY = '#002564';
const COLOR_ACCENT = '#6399f8';

/** Full-bleed: rompe el padding del wizard padre para ocupar 100vw. */
const FULL_BLEED: React.CSSProperties = {
  position: 'relative',
  left: 'calc(50% - 50vw)',
  width: '100vw',
};

/** Contenedor interno con max-width tipográfico (mismo del paso 4). */
const INNER_WRAP: React.CSSProperties = {
  maxWidth: 1200,
  margin: '0 auto',
  padding: 'clamp(32px, 5vw, 80px) clamp(20px, 5vw, 64px)',
};

export function PagoStep({ token, onBack, onContinue }: Props) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['pago-metodos', token],
    queryFn: () => getMetodosPago(token),
    enabled: !!token,
  });

  const iniciar = useMutation({
    mutationFn: () => iniciarPago(token, {}),
    onSuccess: (r) => {
      if (r.modo === 'redirect' && r.urlCheckout) {
        window.open(r.urlCheckout, '_blank', 'noopener,noreferrer');
      }
      // Sin esperar confirmación del gateway: avanzamos al cierre. El cobro
      // se valida fuera del flow.
      onContinue();
    },
  });

  // Mismo handler que el botón "Quiero que me contacten" del paso 4: marca
  // interesContactoMs2 + revierte planSuperadorElegido a false en el backend.
  // El titular queda en MS1 con flag de "quiere ser contactado para MS2".
  const interesContacto = useMutation({
    mutationFn: () => marcarInteresContactoMs2(token),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['plan-superador', token] });
      qc.invalidateQueries({ queryKey: ['onboarding', token] });
      onContinue();
    },
  });

  if (q.isLoading) {
    return (
      <p className="text-element-mid" style={{ textAlign: 'center' }}>
        Cargando pago…
      </p>
    );
  }
  if (q.isError || !q.data) {
    return (
      <p className="status status-error" style={{ display: 'block' }}>
        No se pudo cargar el pago.
      </p>
    );
  }

  const monto = q.data.monto;
  const plan = q.data.plan;
  const submitting = iniciar.isPending || interesContacto.isPending;

  return (
    <div style={{ width: '100%' }}>
      <PagoHero
        plan={plan}
        monto={monto}
        submitting={submitting}
        iniciarPending={iniciar.isPending}
        contactoPending={interesContacto.isPending}
        onPagar={() => iniciar.mutate()}
        onInteresContacto={() => interesContacto.mutate()}
      />

      {iniciar.isError && (
        <p
          className="status status-error"
          style={{ display: 'block', marginTop: 16, textAlign: 'center' }}
        >
          No pudimos generar el link de pago. Reintentá en unos segundos.
        </p>
      )}
      {interesContacto.isError && (
        <p
          className="status status-error"
          style={{ display: 'block', marginTop: 16, textAlign: 'center' }}
        >
          No pudimos guardar tu elección. Reintentá en unos segundos.
        </p>
      )}

      <div
        className="onboarding__actions"
        style={{ marginTop: 24, justifyContent: 'flex-start' }}
      >
        <button className="btn btn-ghost" onClick={onBack} disabled={submitting}>
          Volver
        </button>
      </div>
    </div>
  );
}

// ────────── Hero ──────────

function PagoHero(props: {
  plan: string;
  monto: number;
  submitting: boolean;
  iniciarPending: boolean;
  contactoPending: boolean;
  onPagar: () => void;
  onInteresContacto: () => void;
}) {
  const { plan, monto, submitting, iniciarPending, contactoPending, onPagar, onInteresContacto } =
    props;
  const [revealRef, revealed] = useInViewport<HTMLElement>({ rootMargin: '-80px 0px' });
  return (
    <section
      ref={revealRef}
      className={revealed ? 'onb-revealed' : undefined}
      style={{
        ...FULL_BLEED,
        background: 'linear-gradient(180deg, #eaf0ff 0%, #eef3ff 40%, #eef3ff 100%)',
      }}
    >
      <div style={INNER_WRAP}>
        <header style={{ textAlign: 'center', marginBottom: 40 }}>
          <p
            style={{
              fontFamily: 'Satoshi, sans-serif',
              fontSize: 13,
              color: COLOR_PRIMARY,
              opacity: 0.7,
              margin: 0,
              letterSpacing: '0.04em',
            }}
          >
            Estás contratando
          </p>
          <h2
            className="onb-title-reveal"
            style={{
              fontFamily: 'Switzer, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(20px, 2.8vw, 36px)',
              lineHeight: 1.15,
              letterSpacing: '-0.03em',
              color: COLOR_PRIMARY,
              textTransform: 'uppercase',
              margin: '6px 0 0',
              display: 'inline-block',
            }}
          >
            {plan}
          </h2>
          <p
            style={{
              fontFamily: 'Satoshi, sans-serif',
              fontSize: 13,
              color: COLOR_PRIMARY,
              opacity: 0.7,
              margin: '14px 0 0',
              letterSpacing: '0.04em',
            }}
          >
            Cuota mensual
          </p>
          <p
            style={{
              fontFamily: 'Switzer, sans-serif',
              fontWeight: 800,
              fontSize: 'clamp(40px, 6.5vw, 88px)',
              lineHeight: 1,
              letterSpacing: '-0.05em',
              color: COLOR_ACCENT,
              margin: '12px 0 0',
            }}
          >
            ${fmtNumeroAR(monto)}
          </p>
          <p
            style={{
              fontFamily: 'Satoshi, sans-serif',
              fontSize: 14,
              color: COLOR_PRIMARY,
              opacity: 0.7,
              margin: '14px 0 0',
              maxWidth: 540,
              marginInline: 'auto',
            }}
          >
            Estás pagando la diferencia entre tu plan base MS1 y el MS2 por cada integrante del
            grupo familiar.
          </p>
        </header>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
            gap: 'clamp(32px, 5vw, 80px)',
            alignItems: 'start',
            marginBottom: 40,
          }}
        >
          {/* Columna IZQUIERDA: card mockup verde con blur de profundidad,
              espejo del paso 4. */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 32 }}>
            <div style={{ position: 'relative' }}>
              <div
                aria-hidden
                style={{
                  position: 'absolute',
                  inset: 0,
                  filter: 'blur(50px)',
                  opacity: 0.34,
                  pointerEvents: 'none',
                  zIndex: 0,
                }}
              >
                <PlanMockupCard variant="verde" />
              </div>
              <div style={{ position: 'relative', zIndex: 1 }}>
                <PlanMockupCard variant="verde" />
              </div>
            </div>
          </div>

          {/* Columna DERECHA: los 2 CTAs principales. */}
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'stretch',
              gap: 12,
              width: '100%',
              justifyContent: 'center',
            }}
          >
            <button
              type="button"
              onClick={onPagar}
              disabled={submitting}
              style={{
                background: COLOR_ACCENT,
                color: 'white',
                border: 'none',
                borderRadius: 800,
                padding: '16px 24px',
                fontFamily: 'Satoshi, sans-serif',
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: '-0.5px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                boxShadow: 'inset 0 2px 0 0 rgba(255,255,255,0.12)',
                opacity: submitting ? 0.6 : 1,
                height: 56,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              <Lock size={16} strokeWidth={2.4} />
              {iniciarPending ? 'Generando link…' : 'Pagar'}
              <ExternalLink size={16} strokeWidth={2.4} />
            </button>
            <button
              type="button"
              onClick={onInteresContacto}
              disabled={submitting}
              style={{
                background: 'white',
                color: COLOR_PRIMARY,
                border: 'none',
                borderRadius: 800,
                padding: '16px 24px',
                fontFamily: 'Satoshi, sans-serif',
                fontSize: 16,
                fontWeight: 700,
                letterSpacing: '-0.5px',
                cursor: submitting ? 'not-allowed' : 'pointer',
                opacity: submitting ? 0.6 : 1,
                height: 56,
              }}
            >
              {contactoPending ? 'Guardando…' : 'Quiero que me contacten'}
            </button>
            <p
              style={{
                fontFamily: 'Satoshi, sans-serif',
                fontSize: 12,
                color: COLOR_PRIMARY,
                opacity: 0.6,
                margin: '6px 0 0',
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Si elegís que te contactemos, un asesor se comunicará con vos en breve.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
